import { Attachment, useModal } from '@billy/design-system'
import React, { createContext, ReactElement, ReactNode, useCallback, useContext, useState } from 'react'

import { SortDirection } from '../../../../../enums/sortDirection'
import { AttachmentFillType } from '../../../enums/attachmentFillType'
import { AttachmentFilterType } from '../../../enums/attachmentFiltertype'
import { AttachmentsSortProperty } from '../../../enums/attachmentsSortProperty'

interface ContextState {
  filters: AttachmentsFilters
  handleFileImageClick: (file: Attachment) => void
  handleFileSelect: (file: Attachment) => void
  idsToSkip?: string[]
  modalId: string
  setFilters: (filters: Partial<AttachmentsFilters>) => void
}

const ChooseAttachmentModalContext = createContext<ContextState | undefined>(undefined)

interface AttachmentsFilters {
  fillType: AttachmentFillType
  page: number
  pageSize: number
  searchQuery: string
  sortDirection: SortDirection
  sortProperty: AttachmentsSortProperty
  type: AttachmentFilterType
}

export const defaultChooseAttachmentModalFilters: AttachmentsFilters = {
  fillType: AttachmentFillType.Nonfilled,
  page: 1,
  pageSize: 40,
  searchQuery: '',
  sortDirection: SortDirection.Desc,
  sortProperty: AttachmentsSortProperty.CreatedTime,
  type: AttachmentFilterType.Upload,
}

interface ChooseAttachmentModalContextProps {
  children: ReactNode
  idsToSkip?: string[]
  modalId: string
  onFileImageClick?: (attachment: Attachment) => void
  onSelect: (attachment: Attachment) => void
}

export const ChooseAttachmentModalContextProvider = ({
  children,
  idsToSkip = [],
  modalId,
  onFileImageClick,
  onSelect,
}: ChooseAttachmentModalContextProps): ReactElement => {
  const [filters, setFilters] = useState<AttachmentsFilters>({
    ...defaultChooseAttachmentModalFilters,
    type: AttachmentFilterType.All,
  })
  const { close } = useModal(modalId)

  const handleSetFilters = useCallback(
    (newFilters: Partial<AttachmentsFilters>) => {
      setFilters({ ...filters, ...newFilters })
    },
    [setFilters, filters],
  )

  const handleFileSelect = useCallback(
    (file: Attachment) => {
      onSelect?.(file)
      close()
    },
    [close, onSelect],
  )

  const handleFileImageClick = useCallback(
    (file: Attachment) => {
      onFileImageClick?.(file)
    },
    [onFileImageClick],
  )

  return (
    <ChooseAttachmentModalContext.Provider
      value={{
        filters,
        handleFileImageClick,
        handleFileSelect,
        idsToSkip,
        modalId,
        setFilters: handleSetFilters,
      }}
    >
      {children}
    </ChooseAttachmentModalContext.Provider>
  )
}

export const useChooseAttachmentModal = () => {
  const context = useContext(ChooseAttachmentModalContext)

  if (!context) {
    throw new Error('ChooseAttachmentModalContextProvider is missing in the module!')
  }

  return context
}
